import {
  MutationResendEmailConfirmationArgs,
  MutationResendOtherEmailConfirmationArgs
} from "@outschool/gql-backend-generated";
import { createDayjs } from "@outschool/time";
import { useAnalytics } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { useStripe } from "@stripe/react-stripe-js";
// eslint-disable-next-line import/named
import { StripeCardElement, StripeCardNumberElement } from "@stripe/stripe-js";
import gql from "graphql-tag";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-apollo";

import {
  CreateSetupIntentMutation,
  CreateSetupIntentMutationVariables,
  CurrentUserHasEnrolledQuery,
  CurrentUserHasEnrolledQueryVariables,
  CurrentUserPaymentMethodsQuery,
  CurrentUserPaymentMethodsQueryVariables,
  CurrentUserPricingOfferAdminQuery,
  CurrentUserPricingOfferAdminQueryVariables,
  CurrentUserPricingOffersQuery,
  CurrentUserPricingOffersQueryVariables,
  CurrentUserQueryQuery,
  ResendEmailConfirmationMutation,
  ResendOtherEmailConfirmationMutation
} from "../../shared/generated/graphql";
import {
  resendEmailConfirmationMutation,
  resendOtherEmailConfirmation
} from "../components/emailWarningQueries";

export const CurrentUserHasEnrolled = gql`
  query CurrentUserHasEnrolled {
    currentUser {
      uid
      hasEnrolled
    }
  }
`;

type UseHasEnrolledArgs = {
  skip?: boolean;
  cache?: boolean;
};

export function useHasEnrolled(props?: UseHasEnrolledArgs) {
  const { isLoggedIn } = useSession();
  const { data } = useQuery<
    CurrentUserHasEnrolledQuery,
    CurrentUserHasEnrolledQueryVariables
  >(CurrentUserHasEnrolled, {
    skip: !isLoggedIn || props?.skip,
    fetchPolicy: props?.cache ? "cache-first" : "cache-and-network"
  });
  const hasEnrolled = data?.currentUser?.hasEnrolled ?? props?.skip ?? false;
  return React.useMemo(
    () => isLoggedIn && hasEnrolled,
    [isLoggedIn, hasEnrolled]
  );
}

export const CurrentUserPricingOfferAdmin = gql`
  query CurrentUserPricingOfferAdmin {
    currentUser {
      uid
      pricingOfferAdmin {
        organization {
          uid
        }
      }
    }
  }
`;

type UsePricingOfferAdminArgs = {
  cache?: boolean;
};

export function usePricingOfferAdmin(props?: UsePricingOfferAdminArgs) {
  const { isLoggedIn } = useSession();
  const { data } = useQuery<
    CurrentUserPricingOfferAdminQuery,
    CurrentUserPricingOfferAdminQueryVariables
  >(CurrentUserPricingOfferAdmin, {
    skip: !isLoggedIn,
    fetchPolicy: props?.cache ? "cache-first" : "cache-and-network"
  });
  const pricingOfferAdminOrgUid =
    data?.currentUser?.pricingOfferAdmin?.organization?.uid;
  return React.useMemo(
    () => (isLoggedIn ? pricingOfferAdminOrgUid : undefined),
    [isLoggedIn, pricingOfferAdminOrgUid]
  );
}

const CurrentUserPaymentMethods = gql`
  query CurrentUserPaymentMethods {
    currentUser {
      uid
      paymentMethods {
        uid
        type
        brand
        last4
        expirationMonth
        expirationYear
      }
    }
  }
`;

export function usePaymentMethods() {
  const { isLoggedIn } = useSession();
  const { error, loading, data, refetch } = useQuery<
    CurrentUserPaymentMethodsQuery,
    CurrentUserPaymentMethodsQueryVariables
  >(CurrentUserPaymentMethods, {
    skip: !isLoggedIn,
    // TODO(jess): we can remove this once settleOrder flushes cached sources
    fetchPolicy: "network-only"
  });
  return {
    refetch,
    error,
    loading,
    paymentMethods: data?.currentUser.paymentMethods ?? []
  };
}

const DeletePaymentMethod = gql`
  mutation DeletePaymentMethod($paymentMethodId: ID!) {
    deletePaymentMethod(paymentMethodId: $paymentMethodId)
  }
`;

export function useDeletePaymentMethod(): [
  (paymentMethodId: string) => Promise<void>,
  { error?: Error; loading: boolean }
] {
  const [mutate, { error, loading }] = useMutation(DeletePaymentMethod, {});
  const wrappedMutation = React.useCallback(
    async (paymentMethodId: string) => {
      await mutate({
        variables: { paymentMethodId },
        update: cache => {
          const data = cache.readQuery<
            CurrentUserPaymentMethodsQuery,
            CurrentUserPaymentMethodsQueryVariables
          >({ query: CurrentUserPaymentMethods });
          const paymentMethods = data.currentUser.paymentMethods.filter(
            paymentMethod => paymentMethod.uid !== paymentMethodId
          );
          cache.writeQuery({
            query: CurrentUserPaymentMethods,
            data: {
              ...data,
              currentUser: {
                ...data.currentUser,
                paymentMethods
              }
            }
          });
        }
      });
    },
    [mutate]
  );
  return [wrappedMutation, { error, loading }];
}

const CreateSetupIntent = gql`
  mutation CreateSetupIntent {
    createSetupIntent {
      setupIntentClientId
    }
  }
`;

type CreateSetupIntentArgs = {
  card: StripeCardElement | StripeCardNumberElement;
  zipcode: string;
};

export function useCreateSetupIntent(): [
  ({ card, zipcode }: CreateSetupIntentArgs) => Promise<void>,
  { error?: Error; loading: boolean }
] {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error>(null);
  const stripe = useStripe();
  const [createSetupIntentMutation] = useMutation<
    CreateSetupIntentMutation,
    CreateSetupIntentMutationVariables
  >(CreateSetupIntent, {});
  const wrappedMutation = React.useCallback(
    async ({ card, zipcode }: CreateSetupIntentArgs) => {
      setLoading(true);
      try {
        const {
          data: {
            createSetupIntent: { setupIntentClientId }
          }
        } = await createSetupIntentMutation();
        const { error } = await stripe.confirmCardSetup(setupIntentClientId, {
          payment_method: {
            card,
            billing_details: {
              address: {
                postal_code: zipcode
              }
            }
          }
        });
        if (error) {
          throw error;
        }
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [createSetupIntentMutation, stripe]
  );
  return [wrappedMutation, { error, loading }];
}

export function useResendEmailConfirmation(
  currentUser: CurrentUserQueryQuery["currentUser"],
  emailToConfirm?: string
) {
  const isNonPrimaryEmail =
    emailToConfirm && currentUser?.email !== emailToConfirm;
  const [resendEmail] = useMutation<
    ResendEmailConfirmationMutation,
    MutationResendEmailConfirmationArgs
  >(resendEmailConfirmationMutation);
  const [resendOtherEmail] = useMutation<
    ResendOtherEmailConfirmationMutation,
    MutationResendOtherEmailConfirmationArgs
  >(resendOtherEmailConfirmation);

  const sendEmail = isNonPrimaryEmail
    ? async () => {
        const {
          data: { resendOtherEmailConfirmation }
        } = await resendOtherEmail({
          variables: {
            userUid: currentUser?.uid,
            otherEmail: emailToConfirm
          }
        });
        return resendOtherEmailConfirmation;
      }
    : async () => {
        const {
          data: { resendEmailConfirmation }
        } = await resendEmail({
          variables: { userUid: currentUser?.uid }
        });
        return resendEmailConfirmation;
      };
  return sendEmail;
}

type CurrentUserAttribution = {
  anonymousId?: string;
  wasReferred?: boolean;
};

export function useAttribution() {
  const [attribution, setAttribution] = useState<CurrentUserAttribution>({});
  const { currentUser, isLoggedIn } = useSession();
  const analytics = useAnalytics();

  useEffect(() => {
    async function updateAttribution() {
      if (isLoggedIn && currentUser) {
        setAttribution({
          anonymousId: currentUser.anonymousId,
          wasReferred: currentUser.wasReferred
        });
      }

      if (!isLoggedIn) {
        const attribution = await analytics.attribution();
        setAttribution({
          anonymousId: await analytics.anonymousId(),
          wasReferred:
            !!attribution?.usid || !!attribution?.addressBarUserSlugId
        });
      }
    }

    updateAttribution();
  }, [analytics, currentUser, isLoggedIn]);
  return attribution;
}

export function useCanRequestListing({
  hasPublishedAClass = false
}: {
  hasPublishedAClass?: boolean;
}) {
  const { currentUser } = useSession();

  const teacherBioComplete =
    currentUser && currentUser.details && Boolean(currentUser.details.about);
  const requiredTrainingCompleteForTeachers =
    currentUser &&
    currentUser.hasCompletedClassContentPoliciesTraining &&
    currentUser.hasCompletedSafetyAndPrivacyTraining;
  const requiredTrainingCompleteForOrgAdmin =
    currentUser &&
    currentUser.hasCompletedClassContentPoliciesOrgAdminTraining &&
    currentUser.hasCompletedSafetyAndPrivacyOrgAdminTraining;
  const isApprovedTeacher =
    currentUser && Boolean(currentUser.teacher_approved_at);
  const isSellerOrg = currentUser?.sellerOrg?.currentUserIsOwner;
  const canRequestListing =
    (isSellerOrg && requiredTrainingCompleteForOrgAdmin) ||
    (teacherBioComplete &&
      requiredTrainingCompleteForTeachers &&
      isApprovedTeacher) ||
    hasPublishedAClass;
  return canRequestListing;
}

export const CurrentUserPricingOffers = gql`
  query CurrentUserPricingOffers {
    currentUser {
      uid
      pricingOffers {
        uid
        name
        startAfter
        endBy
        buyerOrgName
        remainingCapCents
        discount
        isFinancialAid
        deletedAt
      }
    }
  }
`;

type UsePricingOffersArgs = {
  skip?: boolean;
};

export function usePricingOffers(props?: UsePricingOffersArgs) {
  const { isLoggedIn } = useSession();
  const { data } = useQuery<
    CurrentUserPricingOffersQuery,
    CurrentUserPricingOffersQueryVariables
  >(CurrentUserPricingOffers, {
    skip: !isLoggedIn || props?.skip
  });

  const currentTime = createDayjs();
  const pricingOffers = data?.currentUser?.pricingOffers ?? [];
  const filteredPricingOffers = pricingOffers.filter(
    pricingOffer =>
      currentTime.isAfter(createDayjs(pricingOffer.startAfter)) &&
      currentTime.isBefore(createDayjs(pricingOffer.endBy))
  );

  return React.useMemo(
    () => (isLoggedIn ? filteredPricingOffers : undefined),
    [isLoggedIn, filteredPricingOffers]
  );
}
